import { Box, Typography } from '@mui/material';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    Proposal,
    SelectedProposal,
} from 'entities/Proposal/model/types/ProposalSchema';
import { getPropertyVendorsMap } from 'entities/Vendors/model/selectors/vendorsSelectors';
import { getPrice } from 'shared/lib/getPrice/getPrice';
import { SupplierLogo } from 'shared/ui/SupplierLogo';

interface SourcingTooltipProps {
    selectedProposals: SelectedProposal[];
    proposals: Proposal[];
    onViewMoreSuppliers?: () => void;
}

const dimmedColor = 'rgba(0, 0, 0, 0.5)';
const rowBorder = '1px solid #D9D9D9';

export const SourcingTooltip = ({
    selectedProposals,
    proposals,
    onViewMoreSuppliers,
}: SourcingTooltipProps) => {
    const { t } = useTranslation();
    const vendorsMap = useSelector(getPropertyVendorsMap);

    const activeSelected =
        selectedProposals?.filter((p) => (p.selectedQuantity ?? 0) > 0) ?? [];

    const selectedSellerUids = new Set(activeSelected.map((p) => p.sellerUid));
    const otherSuppliersCount = (proposals ?? []).filter(
        (p) => !selectedSellerUids.has(p.seller.uid),
    ).length;

    return (
        <Box
            data-testclass="order-item-seller-section-tooltip"
            onClick={(e) => e.stopPropagation()}
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr auto auto',
                alignItems: 'baseline',
                columnGap: '24px',
                minWidth: 280,
            }}
        >
            {/* Header */}
            <Typography
                typography="openSans.subtitle2"
                color={dimmedColor}
                sx={{ pb: '8px' }}
            >
                {t('Sourcing')}
            </Typography>
            <Typography
                typography="openSans.caption"
                color={dimmedColor}
                sx={{ pb: '8px', textAlign: 'right' }}
            >
                {t('Qty')}
            </Typography>
            <Typography
                typography="openSans.caption"
                color={dimmedColor}
                sx={{ pb: '8px', textAlign: 'right' }}
            >
                {t('Total')}
            </Typography>

            {/* Rows */}
            {activeSelected.map((proposal, index) => {
                const vendor = vendorsMap?.[proposal.sellerUid];
                const total =
                    (proposal.price ?? 0) * (proposal.selectedQuantity ?? 0);

                return (
                    <Fragment key={proposal.uid}>
                        {index > 0 && (
                            <Box
                                sx={{
                                    gridColumn: '1 / -1',
                                    borderTop: rowBorder,
                                }}
                            />
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                py: '14px',
                                minWidth: 0,
                            }}
                        >
                            <SupplierLogo
                                vendor={vendor}
                                title={proposal.name}
                                size={26}
                            />
                            <Typography
                                typography="openSans.subtitle2"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {proposal.name}
                            </Typography>
                        </Box>
                        <Typography
                            typography="openSans.body2"
                            sx={{
                                py: '14px',
                                textAlign: 'right',
                                alignSelf: 'center',
                            }}
                        >
                            {proposal.selectedQuantity}
                        </Typography>
                        <Typography
                            typography="openSans.body2"
                            sx={{
                                py: '14px',
                                textAlign: 'right',
                                alignSelf: 'center',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {getPrice(total)}
                        </Typography>
                    </Fragment>
                );
            })}

            {/* Footer */}
            {otherSuppliersCount > 0 && (
                <Typography
                    data-testclass="order-item-other-sellers-link"
                    typography="openSans.body2"
                    onClick={onViewMoreSuppliers}
                    sx={{
                        gridColumn: '1 / -1',
                        color: '#3F6EB7',
                        borderTop: rowBorder,
                        pt: '12px',
                        mt: '4px',
                        ...(onViewMoreSuppliers && {
                            cursor: 'pointer',
                            '&:hover': { textDecoration: 'underline' },
                        }),
                    }}
                >
                    {otherSuppliersCount} {t('other suppliers available')}
                </Typography>
            )}
        </Box>
    );
};
